import type { MatterSignatureRequestListItem } from "@/components/features/matter/file-repo/signature-requests/MatterSignatureRequestItem";
import { MatterSignatureRequestItem } from "@/components/features/matter/file-repo/signature-requests/MatterSignatureRequestItem";
import {
  ActionEmptyState,
  AppPageShell,
  Badge,
  Card,
  CardHeader,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Typography,
} from "@/components/ui";
import LawWorksLoader from "@/components/ui/LawWorksLoader/LawWorksLoader";
import { PageHighlightCards } from "@/components/ui/PageHighlightsCards/PageHighlightCards";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveSignatureRequestsV2QueryOptions } from "@/lib/data/react-query/signature-requests/signature-request";
import type {
  SignatureRequestListStatus,
  SignatureRequestListV2Response,
} from "@/lib/data/requests/signature-requests/signature-request";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import type { ReportsSignatureRequestReport } from "@/lib/types/Reports";
import { formatContactsName } from "@/lib/utils/format";
import { FileSignatureRequestIcon } from "@/lib/utils/iconUtils";
import {
  createMinimalMatterStubForSignatureRequestV2,
  displaySignatureRequestUserSignerName,
  mapFirmSignatureRequestV2ToReportRow,
  mapSignatureRequestV2ToListItem,
} from "@/lib/utils/matters/signature-request-report-utils";
import {
  invalidateSignatureRequestCreateRelatedQueries,
  SIGNATURE_REQUEST_CREATED_EVENT,
  SIGNATURE_REQUEST_REPORT_POST_CREATE_LOADING_MS,
} from "@/lib/utils/signature-request-created";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import Fuse from "fuse.js";
import { useEffect, useMemo, useRef, useState } from "react";

type SignatureRequestStatusTab = "outstanding" | "expired" | "completed";

type SignatureRequestFilterTab = SignatureRequestStatusTab | "all";

type GlobalSignatureRequestRow =
  | {
      kind: "matter";
      tab: SignatureRequestStatusTab;
      matter: Matter;
      matterTitle: string;
      signatureRequest: MatterSignatureRequestListItem;
    }
  | {
      kind: "firm";
      tab: SignatureRequestStatusTab;
      matter: Matter;
      signatureRequest: MatterSignatureRequestListItem;
      reportRow: ReportsSignatureRequestReport;
    };

const getTabForMatterSignatureRequest = (
  signatureRequest: MatterSignatureRequestListItem,
): SignatureRequestStatusTab => {
  const contactSigners = signatureRequest.matterSignatureRequestContactSigners;
  const userSigners = signatureRequest.matterSignatureRequestUserSigners;

  // Match `MatterSignatureRequestItem` behavior:
  // - If there are no signers, treat as outstanding (not completed / expired).
  if (contactSigners.length === 0 && userSigners.length === 0) {
    return "outstanding";
  }

  const allSigned =
    contactSigners.every((signer) => !!signer.signatureRequestComplete) &&
    userSigners.every((signer) => !!signer.signatureRequestComplete);

  // Completed overrides expired.
  if (allSigned) {
    return "completed";
  }

  const expirationDate = signatureRequest.matterSignatureRequestExpirationDate;
  const hasExpired =
    Boolean(expirationDate) && dayjs().isAfter(dayjs(expirationDate));
  if (hasExpired) {
    return "expired";
  }

  return "outstanding";
};

const getSignatureRequestStatusLabel = (
  signatureRequest: MatterSignatureRequestListItem,
): "Completed" | "Expired" | "Pending" | "No Signers" => {
  const contactSigners = signatureRequest.matterSignatureRequestContactSigners;
  const userSigners = signatureRequest.matterSignatureRequestUserSigners;

  if (contactSigners.length === 0 && userSigners.length === 0) {
    return "No Signers";
  }

  const allSigned =
    contactSigners.every((signer) => !!signer.signatureRequestComplete) &&
    userSigners.every((signer) => !!signer.signatureRequestComplete);

  if (allSigned) {
    return "Completed";
  }

  const expirationDate = signatureRequest.matterSignatureRequestExpirationDate;
  const hasExpired =
    Boolean(expirationDate) && dayjs().isAfter(dayjs(expirationDate));
  if (hasExpired) {
    return "Expired";
  }

  return "Pending";
};

const createRowsByTab = (
  signatureRequestsData?: SignatureRequestListV2Response,
) => {
  const buckets: Record<
    SignatureRequestStatusTab,
    GlobalSignatureRequestRow[]
  > = {
    outstanding: [],
    expired: [],
    completed: [],
  };

  for (const request of signatureRequestsData?.data.matterSignatureRequests ??
    []) {
    const listItem = mapSignatureRequestV2ToListItem(request);
    const tab = getTabForMatterSignatureRequest(listItem);

    buckets[tab].push({
      kind: "matter",
      tab,
      matter: createMinimalMatterStubForSignatureRequestV2(request.matterId),
      matterTitle: "Matter",
      signatureRequest: listItem,
    });
  }

  for (const request of signatureRequestsData?.data.firmSignatureRequests ??
    []) {
    const listItem = mapSignatureRequestV2ToListItem(request);
    const tab = getTabForMatterSignatureRequest(listItem);

    buckets[tab].push({
      kind: "firm",
      tab,
      matter: createMinimalMatterStubForSignatureRequestV2(""),
      reportRow: mapFirmSignatureRequestV2ToReportRow(request),
      signatureRequest: listItem,
    });
  }

  for (const tab of Object.keys(buckets) as SignatureRequestStatusTab[]) {
    buckets[tab].sort((a, b) =>
      b.signatureRequest.dateCreated.localeCompare(
        a.signatureRequest.dateCreated,
      ),
    );
  }

  return buckets;
};

export function GlobalSignatureRequestsView() {
  const queryClient = useQueryClient();
  const { hasPagePermission, hasActionPermission } = useUser();
  const { openCreateSignatureRequestModal } = useGlobalModal();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.signatureRequestsAccess,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSignatureRequest,
    ACTION_PERMISSION_VALUES.create,
  );

  const [activeTab, setActiveTab] = useState<SignatureRequestFilterTab>("all");
  const [query, setQuery] = useState("");

  const activeStatus: SignatureRequestListStatus | undefined =
    activeTab === "all"
      ? undefined
      : activeTab === "outstanding"
        ? "pending"
        : activeTab;
  const { data: allSignatureRequestsData, isLoading: isAllRequestsLoading } =
    useQuery({
      ...retrieveSignatureRequestsV2QueryOptions({ pageSize: 500 }),
      enabled: hasViewPermission,
    });
  const {
    data: filteredSignatureRequestsData,
    isFetching: isFilteredRequestsFetching,
  } = useQuery({
    ...retrieveSignatureRequestsV2QueryOptions({
      pageSize: 500,
      status: activeStatus,
    }),
    enabled: hasViewPermission && activeStatus !== undefined,
    placeholderData: (previousData) => previousData,
  });

  const [postCreateLoading, setPostCreateLoading] = useState(false);
  const postCreateLoadingTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    let cancelled = false;

    const onCreated = () => {
      setActiveTab((currentTab) =>
        currentTab === "outstanding" ? "outstanding" : "all",
      );
      setPostCreateLoading(true);
      if (postCreateLoadingTimeoutRef.current) {
        clearTimeout(postCreateLoadingTimeoutRef.current);
      }
      postCreateLoadingTimeoutRef.current = setTimeout(() => {
        void (async () => {
          await invalidateSignatureRequestCreateRelatedQueries(queryClient);
          if (!cancelled) {
            setPostCreateLoading(false);
          }
          postCreateLoadingTimeoutRef.current = null;
        })();
      }, SIGNATURE_REQUEST_REPORT_POST_CREATE_LOADING_MS);
    };

    window.addEventListener(SIGNATURE_REQUEST_CREATED_EVENT, onCreated);
    return () => {
      cancelled = true;
      window.removeEventListener(SIGNATURE_REQUEST_CREATED_EVENT, onCreated);
      if (postCreateLoadingTimeoutRef.current) {
        clearTimeout(postCreateLoadingTimeoutRef.current);
      }
    };
  }, [queryClient, setActiveTab]);

  const isResultsLoading =
    isAllRequestsLoading ||
    (activeStatus !== undefined && isFilteredRequestsFetching) ||
    postCreateLoading;

  const allRowsByTab = useMemo(
    () => createRowsByTab(allSignatureRequestsData),
    [allSignatureRequestsData],
  );
  const filteredRowsByTab = useMemo(
    () => createRowsByTab(filteredSignatureRequestsData),
    [filteredSignatureRequestsData],
  );

  const allRowsSorted = useMemo(() => {
    const combined = [
      ...allRowsByTab.outstanding,
      ...allRowsByTab.expired,
      ...allRowsByTab.completed,
    ];
    return combined.sort((a, b) => {
      const aDate = a.signatureRequest.dateCreated;
      const bDate = b.signatureRequest.dateCreated;
      return bDate.localeCompare(aDate);
    });
  }, [allRowsByTab]);

  const tabCounts = useMemo(
    () => ({
      outstanding: allRowsByTab.outstanding.length,
      expired: allRowsByTab.expired.length,
      completed: allRowsByTab.completed.length,
      all: allRowsSorted.length,
    }),
    [
      allRowsSorted.length,
      allRowsByTab.completed.length,
      allRowsByTab.expired.length,
      allRowsByTab.outstanding.length,
    ],
  );

  const visibleRows =
    activeTab === "all" ? allRowsSorted : filteredRowsByTab[activeTab];

  const filteredVisibleRows = useMemo(() => {
    const sanitizedQuery = query.trim();
    if (!sanitizedQuery) {
      return visibleRows;
    }

    const searchable = visibleRows.map((row) => {
      const signatureRequest = row.signatureRequest;

      const contactSignerNames =
        signatureRequest.matterSignatureRequestContactSigners
          .map((signer) => formatContactsName(signer.contact))
          .filter(Boolean)
          .join(" ");
      const contactSignerEmails =
        signatureRequest.matterSignatureRequestContactSigners
          .flatMap((signer) => signer.contact.emails)
          .map((email) => email.address)
          .filter(Boolean)
          .join(" ");
      const userSignerNames = signatureRequest.matterSignatureRequestUserSigners
        .map((signer) => displaySignatureRequestUserSignerName(signer))
        .filter(Boolean)
        .join(" ");
      const userSignerEmails =
        signatureRequest.matterSignatureRequestUserSigners
          .map((signer) =>
            typeof signer.email === "string" ? signer.email : "",
          )
          .filter(Boolean)
          .join(" ");
      const userSignerPhones =
        signatureRequest.matterSignatureRequestUserSigners
          .map((signer) =>
            typeof signer.phoneNumber === "string" ? signer.phoneNumber : "",
          )
          .filter(Boolean)
          .join(" ");

      const statusLabel = getSignatureRequestStatusLabel(signatureRequest);

      return {
        row,
        kind: row.kind,
        matterTitle: row.kind === "matter" ? row.matterTitle : "Firm",
        matterFileName: signatureRequest.matterFileName,
        statusLabel,
        expirationDate: signatureRequest.matterSignatureRequestExpirationDate,
        contactSignerNames,
        contactSignerEmails,
        userSignerNames,
        userSignerEmails,
        userSignerPhones,
      };
    });

    const fuse = new Fuse(searchable, {
      keys: [
        { name: "matterFileName", weight: 0.55 },
        { name: "matterTitle", weight: 0.25 },
        { name: "statusLabel", weight: 0.2 },
        { name: "contactSignerNames", weight: 0.2 },
        { name: "contactSignerEmails", weight: 0.15 },
        { name: "userSignerNames", weight: 0.15 },
        { name: "userSignerEmails", weight: 0.12 },
        { name: "userSignerPhones", weight: 0.1 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
    });

    return fuse.search(sanitizedQuery).map((result) => result.item.row);
  }, [query, visibleRows]);

  const totalSignatureRequests = useMemo(
    () => filteredVisibleRows.length,
    [filteredVisibleRows.length],
  );

  const visibleMatterCount = useMemo(() => {
    const ids = new Set<string>();
    for (const row of filteredVisibleRows) {
      if (row.kind === "matter") {
        ids.add(row.matter.id);
      }
    }
    return ids.size;
  }, [filteredVisibleRows]);

  const pageHighlightCards = useMemo(
    () => [
      {
        title: "Signature requests",
        value: totalSignatureRequests,
      },
      {
        title: "Matters",
        value: visibleMatterCount,
      },
    ],
    [totalSignatureRequests, visibleMatterCount],
  );

  if (!hasViewPermission && !hasCreatePermission) {
    return null;
  }

  return (
    <AppPageShell
      className="h-full overflow-hidden"
      contentClassName="flex min-h-0 flex-col gap-y-4 overflow-hidden"
    >
      <>
        <div className="bg-background/95 supports-[backdrop-filter]:bg-background/80 sticky top-0 z-20 -mx-0 pb-4 backdrop-blur">
          <Text as="h1" variant="title" className="mb-6 shrink-0">
            Signature requests
          </Text>
          {hasViewPermission ? (
            <PageHighlightCards items={pageHighlightCards} variant="summary" />
          ) : null}

          {hasViewPermission || hasCreatePermission ? (
            <div className="mt-4 flex w-full flex-wrap items-center justify-between gap-3">
              {hasViewPermission ? (
                <Tabs
                  value={activeTab}
                  onValueChange={(v) => {
                    setActiveTab(v as SignatureRequestFilterTab);
                  }}
                  className="min-w-0 flex-1 self-center"
                >
                  <TabsList className="w-fit items-center justify-start">
                    <TabsTrigger value="all">All ({tabCounts.all})</TabsTrigger>
                    <TabsTrigger value="outstanding">
                      Outstanding ({tabCounts.outstanding})
                    </TabsTrigger>
                    <TabsTrigger value="expired">
                      Expired ({tabCounts.expired})
                    </TabsTrigger>
                    <TabsTrigger value="completed">
                      Completed ({tabCounts.completed})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="all" />
                  <TabsContent value="outstanding" />
                  <TabsContent value="expired" />
                  <TabsContent value="completed" />
                </Tabs>
              ) : (
                <div className="min-w-0 flex-1" />
              )}
              {hasViewPermission ? (
                <div className="w-full max-w-[380px]">
                  <Input
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                    }}
                    placeholder="Search file, matter, signer, status"
                  />
                </div>
              ) : null}
              {hasCreatePermission ? (
                <Button
                  onClick={() => {
                    openCreateSignatureRequestModal({});
                  }}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
                >
                  <FileSignatureRequestIcon className="h-4 w-4" />
                  Create signature request
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto pt-4">
          {!hasViewPermission ? (
            <Card className="w-full border-2">
              <CardHeader className="space-y-1">
                <Typography
                  variant="subHeading"
                  className="text-muted-foreground"
                >
                  You do not have permission to view signature requests.
                </Typography>
              </CardHeader>
            </Card>
          ) : visibleRows.length === 0 ? (
            <ActionEmptyState
              title={
                allRowsSorted.length === 0
                  ? "No signature requests yet"
                  : `No ${activeTab[0].toUpperCase()}${activeTab.slice(1)} signature requests`
              }
              description={
                allRowsSorted.length === 0
                  ? "Create one from here to manage signatures across all matters."
                  : `There are no ${activeTab} signature requests to show.`
              }
              actions={
                allRowsSorted.length === 0 && hasCreatePermission ? (
                  <Button
                    onClick={() => {
                      openCreateSignatureRequestModal({});
                    }}
                  >
                    <FileSignatureRequestIcon className="h-4 w-4" />
                    Create signature request
                  </Button>
                ) : null
              }
            />
          ) : filteredVisibleRows.length === 0 ? (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1">
                <Typography
                  variant="heading"
                  className="text-muted-foreground text-center"
                >
                  No results found.
                </Typography>
                <Typography
                  variant="default"
                  className="text-muted-foreground text-center text-sm"
                >
                  Try a different search term.
                </Typography>
              </CardHeader>
            </Card>
          ) : (
            <div className="flex flex-col gap-y-6 pb-4">
              {filteredVisibleRows.map((row) =>
                row.kind === "matter" ? (
                  <MatterSignatureRequestItem
                    key={row.signatureRequest.matterSignatureRequestId}
                    matter={row.matter}
                    signatureRequest={row.signatureRequest}
                    signatureRequestPresentation="modal"
                    showSubItemControls={false}
                    contextLabel={
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">Matter</Badge>
                        <Typography className="text-muted-foreground text-sm">
                          {row.matterTitle}
                        </Typography>
                      </div>
                    }
                  />
                ) : (
                  <MatterSignatureRequestItem
                    key={row.signatureRequest.matterSignatureRequestId}
                    matter={row.matter}
                    signatureRequest={row.signatureRequest}
                    firmSignatureReportRow={row.reportRow}
                    contextLabel={
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">Firm</Badge>
                        <Typography className="text-muted-foreground text-sm">
                          Firm signature request
                        </Typography>
                      </div>
                    }
                  />
                ),
              )}
            </div>
          )}
          {hasViewPermission && isResultsLoading ? (
            <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]">
              <LawWorksLoader className="w-full" />
            </div>
          ) : null}
        </div>
      </>
    </AppPageShell>
  );
}
